import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import {
  ArrowLeft, Edit2, Lock, DollarSign, FileText, TrendingUp,
  Package, CheckCircle2, ArrowRightCircle, Building2,
  Clock, Send, CheckCircle, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  PENDENTE:   { label: 'Pendente',   cls: 'bg-amber-100 text-amber-800 border-amber-300',      icon: Clock },
  ENVIADO:    { label: 'Enviado',    cls: 'bg-blue-100 text-blue-800 border-blue-300',          icon: Send },
  APROVADO:   { label: 'Aprovado',   cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle },
  RECUSADO:   { label: 'Recusado',   cls: 'bg-red-100 text-red-700 border-red-300',             icon: XCircle },
  CONVERTIDO: { label: 'Convertido', cls: 'bg-purple-100 text-purple-700 border-purple-300',    icon: CheckCircle },
};

const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmt = (v, c = 2) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c }).format(v || 0);
const fmtDate = (d) => { if (!d) return '---'; return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { timeZone: 'UTC' }); };
const fmtDateTime = (d) => {
  if (!d) return '---';
  return new Date(d).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};
const lbl = "text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block";

export default function OrcamentoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [orc, setOrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [convertendo, setConvertendo] = useState(false);

  const fetchOrc = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orcamentos/${id}`);
      setOrc(data);
    } catch {
      toast.error('Orçamento não encontrado');
      navigate('/orcamentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrc();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConverter = async () => {
    if (!window.confirm('Confirma a conversão deste orçamento em pedido?')) return;
    setConvertendo(true);
    try {
      await api.post(`/orcamentos/${id}/converter-em-pedido`);
      toast.success('Orçamento convertido em pedido!');
      await fetchOrc();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao converter');
    } finally {
      setConvertendo(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Orçamento...</p>
    </div>
  );

  if (!orc) return null;

  const convertido = orc.status === 'CONVERTIDO';
  const statusCfg = STATUS_CONFIG[orc.status] || STATUS_CONFIG.PENDENTE;
  const StatusIcon = statusCfg.icon;
  const itens = orc.itens || [];

  const timelineEvents = [
    orc.criado_em && {
      icon: Package,
      label: 'Proposta criada',
      date: fmtDateTime(orc.criado_em),
      by: orc.criado_por,
      color: 'text-blue-600 bg-blue-50 border-blue-200',
    },
    orc.atualizado_em && {
      icon: Edit2,
      label: 'Atualizado',
      date: fmtDateTime(orc.atualizado_em),
      by: orc.atualizado_por,
      color: 'text-amber-600 bg-amber-50 border-amber-200',
    },
    convertido && orc.pedido_id && {
      icon: CheckCircle2,
      label: 'Convertido em pedido',
      date: orc.atualizado_em ? fmtDateTime(orc.atualizado_em) : '---',
      by: null,
      color: 'text-purple-600 bg-purple-50 border-purple-200',
    },
  ].filter(Boolean);

  return (
    <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/orcamentos')}
            className="flex items-center gap-1.5 text-xs font-black uppercase text-slate-500 hover:text-[#0A3D73] transition-colors">
            <ArrowLeft size={14} /> Voltar
          </button>
          <div className="w-px h-5 bg-slate-200" />
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-black text-slate-900 font-mono">
                {orc.numero_proposta || '---'}
              </h1>
              <span className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-black border rounded-full ${statusCfg.cls}`}>
                <StatusIcon size={9} /> {statusCfg.label}
              </span>
            </div>
            <p className="text-sm text-slate-500 font-semibold mt-0.5">{orc.cliente_nome}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {orc.status === 'APROVADO' && (
            <button
              onClick={handleConverter}
              disabled={convertendo}
              className="flex items-center gap-2 text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60">
              <ArrowRightCircle size={13} />
              {convertendo ? 'Convertendo...' : 'Converter em Pedido'}
            </button>
          )}
          <button
            onClick={() => { if (!convertido) navigate(`/orcamentos/editar/${id}`); }}
            disabled={convertido}
            className={`flex items-center gap-2 text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors ${
              convertido
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-[#0A3D73] hover:bg-[#082D54] text-white'
            }`}>
            {convertido ? <Lock size={13} /> : <Edit2 size={13} />}
            {convertido ? 'Convertido' : 'Editar Orçamento'}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          {
            label: 'Total Geral',
            value: brl(orc.valor_total),
            sub: null,
            icon: DollarSign,
            cor: '#166534',
            fundo: '#DCFCE7',
          },
          {
            label: 'Subtotal',
            value: brl(orc.subtotal),
            sub: null,
            icon: FileText,
            cor: '#1E40AF',
            fundo: '#EFF6FF',
          },
          {
            label: 'IPI',
            value: brl(orc.valor_ipi),
            sub: orc.ipi ? `${fmt(orc.ipi)}%` : null,
            icon: TrendingUp,
            cor: '#92400E',
            fundo: '#FEF3C7',
          },
          {
            label: 'Itens',
            value: itens.length,
            sub: null,
            icon: Package,
            cor: '#6B21A8',
            fundo: '#F3E8FF',
          },
        ].map((k, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{k.label}</p>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: k.fundo }}>
                <k.icon size={13} style={{ color: k.cor }} />
              </div>
            </div>
            <p className="text-lg font-black" style={{ color: k.cor }}>{k.value}</p>
            {k.sub && <p className="text-xs font-bold text-slate-400 mt-0.5">{k.sub}</p>}
          </div>
        ))}
      </div>

      {/* CORPO 2 COLUNAS */}
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6">

        {/* COL ESQUERDA */}
        <div className="space-y-6">

          {/* Card Dados da Proposta */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Dados da Proposta</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className={lbl}>Nº Proposta</p>
                <p className="text-sm font-black font-mono text-slate-800">{orc.numero_proposta || '—'}</p>
              </div>
              <div>
                <p className={lbl}>Data Emissão</p>
                <p className="text-sm font-bold text-slate-700">{fmtDate(orc.data_emissao)}</p>
              </div>
              <div>
                <p className={lbl}>Validade</p>
                <p className="text-sm font-bold text-slate-700">{orc.validade || '—'}</p>
              </div>
              {orc.representada_nome && (
                <div className="col-span-2 md:col-span-3">
                  <p className={lbl}>Representada</p>
                  <p className="text-sm font-bold text-slate-800">{orc.representada_nome}</p>
                </div>
              )}
              <div>
                <p className={lbl}>Criado em</p>
                <p className="text-xs font-bold text-slate-600">{fmtDateTime(orc.criado_em)}</p>
                {orc.criado_por && <p className="text-[10px] text-slate-400 font-semibold mt-0.5">{orc.criado_por}</p>}
              </div>
              {orc.atualizado_em && (
                <div>
                  <p className={lbl}>Atualizado em</p>
                  <p className="text-xs font-bold text-slate-600">{fmtDateTime(orc.atualizado_em)}</p>
                  {orc.atualizado_por && <p className="text-[10px] text-slate-400 font-semibold mt-0.5">{orc.atualizado_por}</p>}
                </div>
              )}
            </div>
          </div>

          {/* Card Dados do Cliente */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Dados do Cliente</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <p className={lbl}>Razão Social</p>
                <p className="text-sm font-bold text-slate-800 uppercase">{orc.cliente_nome || '—'}</p>
              </div>
              {orc.cliente_cnpj && (
                <div>
                  <p className={lbl}>CNPJ</p>
                  <p className="text-sm font-mono font-bold text-slate-700">{orc.cliente_cnpj}</p>
                </div>
              )}
              {orc.cliente_contato && (
                <div>
                  <p className={lbl}>Contato</p>
                  <p className="text-sm font-bold text-slate-700">{orc.cliente_contato}</p>
                </div>
              )}
              {orc.cliente_telefone && (
                <div>
                  <p className={lbl}>Telefone</p>
                  <p className="text-sm font-bold text-slate-700">{orc.cliente_telefone}</p>
                </div>
              )}
              {orc.cliente_endereco && (
                <div className="col-span-2">
                  <p className={lbl}>Endereço</p>
                  <p className="text-sm font-semibold text-slate-700">{orc.cliente_endereco}</p>
                </div>
              )}
            </div>
          </div>

          {/* Card Itens */}
          {itens.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Itens</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left border-b border-slate-100">
                      <th className="pb-2 font-black text-slate-400 uppercase tracking-wide">FE</th>
                      <th className="pb-2 font-black text-slate-400 uppercase tracking-wide">Descrição</th>
                      <th className="pb-2 font-black text-slate-400 uppercase tracking-wide text-right">Qtd Mín</th>
                      <th className="pb-2 font-black text-slate-400 uppercase tracking-wide text-right">Preço/Milhar</th>
                      <th className="pb-2 font-black text-slate-400 uppercase tracking-wide text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map((it, i) => (
                      <tr key={i} className={`border-b border-slate-50 ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
                        <td className="py-2 font-mono font-bold text-blue-700">{it.fe || '—'}</td>
                        <td className="py-2 font-semibold text-slate-700">
                          <div>{it.descricao || '—'}</div>
                          {it.medidas && <div className="text-[10px] text-slate-400">{it.medidas} {it.unidade || ''}</div>}
                        </td>
                        <td className="py-2 font-bold text-slate-700 text-right">{it.qtd_min != null ? Number(it.qtd_min).toLocaleString('pt-BR') : '—'}</td>
                        <td className="py-2 font-bold text-slate-700 text-right">{it.preco_milhar != null ? brl(it.preco_milhar) : '—'}</td>
                        <td className="py-2 font-black text-emerald-700 text-right">{it.subtotal != null ? brl(it.subtotal) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-slate-200">
                      <td colSpan={4} className="pt-3 font-black text-slate-500 uppercase tracking-wide text-xs">Total</td>
                      <td className="pt-3 font-black text-emerald-700 text-right text-sm">{brl(orc.valor_total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Card Condições Comerciais */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Condições Comerciais</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {orc.prazo_pagamento && (
                <div>
                  <p className={lbl}>Prazo Pgto</p>
                  <p className="text-sm font-bold text-slate-700">{orc.prazo_pagamento}</p>
                </div>
              )}
              {orc.frete && (
                <div>
                  <p className={lbl}>Frete</p>
                  <p className="text-sm font-bold text-slate-700">{orc.frete}</p>
                </div>
              )}
              {orc.prazo_entrega && (
                <div>
                  <p className={lbl}>Prazo Entrega</p>
                  <p className="text-sm font-bold text-slate-700">{orc.prazo_entrega}</p>
                </div>
              )}
              {orc.icms && (
                <div>
                  <p className={lbl}>ICMS</p>
                  <p className="text-sm font-bold text-slate-700">{orc.icms}</p>
                </div>
              )}
              {orc.ipi != null && (
                <div>
                  <p className={lbl}>IPI</p>
                  <p className="text-sm font-bold text-slate-700">{fmt(orc.ipi)}%</p>
                </div>
              )}
            </div>
            {orc.observacoes && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className={lbl}>Observações</p>
                <p className="text-sm font-semibold text-slate-700 whitespace-pre-wrap">{orc.observacoes}</p>
              </div>
            )}
          </div>

        </div>

        {/* COL DIREITA */}
        <div className="space-y-6">

          {/* Card Status da Proposta */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Status da Proposta</p>
            <div className="space-y-4">
              <div className="flex justify-center pt-1">
                <span className={`flex items-center gap-2 px-5 py-2 text-sm font-black border-2 rounded-xl ${statusCfg.cls}`}>
                  <StatusIcon size={14} /> {statusCfg.label}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className={lbl}>Emissão</p>
                  <p className="text-sm font-bold text-slate-700">{fmtDate(orc.data_emissao)}</p>
                </div>
                <div>
                  <p className={lbl}>Validade</p>
                  <p className="text-sm font-bold text-slate-700">{orc.validade || '—'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Card Representada */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4 flex items-center gap-2">
              <Building2 size={13} /> Representada
            </p>
            <p className={`text-sm font-bold ${orc.representada_nome ? 'text-slate-800' : 'text-slate-400'}`}>
              {orc.representada_nome || '—'}
            </p>
          </div>

          {/* Card Pedido Vinculado */}
          {convertido && orc.pedido_id && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4 flex items-center gap-2">
                <FileText size={13} /> Pedido Vinculado
              </p>
              <button
                onClick={() => navigate(`/pedidos/${orc.pedido_id}`)}
                className="w-full flex items-center justify-between p-3 rounded-lg border border-purple-200 bg-purple-50 hover:bg-purple-100 transition-colors group">
                <div>
                  <p className="text-xs font-black uppercase tracking-wide text-purple-700">Ver Pedido</p>
                  <p className="text-xs text-purple-500 font-mono mt-0.5">{orc.pedido_id}</p>
                </div>
                <ArrowRightCircle size={16} className="text-purple-400 group-hover:text-purple-700 transition-colors" />
              </button>
            </div>
          )}

          {/* Card Linha do Tempo */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Linha do Tempo</p>
            <div className="space-y-0">
              {timelineEvents.map((ev, i) => (
                <div key={i} className="flex gap-3 pb-4 relative">
                  {i < timelineEvents.length - 1 && (
                    <div className="absolute left-[13px] top-7 bottom-0 w-px bg-slate-200" />
                  )}
                  <div className={`w-7 h-7 rounded-full border flex items-center justify-center flex-shrink-0 ${ev.color}`}>
                    <ev.icon size={12} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-black text-slate-700">{ev.label}</p>
                    <p className="text-[10px] text-slate-400 font-semibold">{ev.date}</p>
                    {ev.by && <p className="text-[10px] text-slate-400">{ev.by}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
